/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   รายงาน PO ค้างรับ — มุมมองปฏิทิน

   ช่องปฏิทินของแต่ละวัน = ใบสั่งซื้อที่นัดส่งของวันนั้นและยังรับไม่ครบ
   กดที่วัน → เปิดรายละเอียดว่ามีใบไหน สินค้าอะไร ค้างอยู่เท่าไร
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import "./po-pending.css";
import {
  Badge,
  Button,
  Calendar,
  Card,
  Flex,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import ReportService from "../../../../service/ERP/Report.service";
import { poPendingColumns } from "./model";
import useOpenDoc from "../../../../hooks/useOpenDoc";
import {
  saveAccessPageStateCookie,
  loadAccessPageStateCookie,
} from "../../../../utils/accessPageStateCookie";

const reportService = ReportService();
/* จำเดือน/ปี และรูปแบบแสดง (เดือน/ปี) ที่เปิดค้างไว้ */
const PAGE_KEY = "erp_po_pending";

export default function PoPendingAccess() {
  const { openPo } = useOpenDoc();

  const saved = useMemo(() => loadAccessPageStateCookie(PAGE_KEY) || {}, []);

  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(
    saved?.month ? dayjs(saved.month) : dayjs()
  );
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);

  /* วันที่ถูกเลือกไว้ดูรายละเอียด (null = ปิดหน้าต่าง) */
  const [pickedDate, setPickedDate] = useState(null);

  const loadData = (m = month) => {
    setLoading(true);
    reportService
      .getPoPending({ month: dayjs(m).format("YYYY-MM") })
      .then((res) => {
        const d = res?.data?.data || {};
        setSummary(d.summary || []);
        setItems(d.items || []);
      })
      .catch(() => message.error("โหลดรายงาน PO ค้างรับไม่สำเร็จ"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData(month);
  }, [month]);

  /* สรุปรายวัน map ด้วย key วันที่ เพื่อหยิบมาวาดในช่องปฏิทินได้เร็ว */
  const summaryMap = useMemo(() => {
    const m = {};
    (summary || []).forEach((s) => {
      m[s.deldate] = s;
    });
    return m;
  }, [summary]);

  const monthTotal = useMemo(
    () =>
      (summary || []).reduce(
        (a, s) => ({
          po: a.po + Number(s.po_count || 0),
          item: a.item + Number(s.item_count || 0),
        }),
        { po: 0, item: 0 }
      ),
    [summary]
  );

  /* ชื่อผู้ขายของแต่ละวัน (ไม่ซ้ำ) เอาไว้แสดงในช่องปฏิทิน */
  const supplierMap = useMemo(() => {
    const m = {};
    (items || []).forEach((i) => {
      const d = i.deldate;
      const name = i.supname || i.supcode || "-";
      if (!m[d]) m[d] = [];
      if (!m[d].includes(name)) m[d].push(name);
    });
    return m;
  }, [items]);

  const dayItems = useMemo(
    () =>
      pickedDate
        ? (items || []).filter((i) => i.deldate === pickedDate)
        : [],
    [pickedDate, items]
  );

  /* เลื่อนเดือน (step) หรือระบุเดือนตรง ๆ แล้วจำไว้ใน cookie */
  const changeMonth = (step = 0, next = null) => {
    const m = next ? dayjs(next) : month.add(step, "month");
    setMonth(m);
    saveAccessPageStateCookie(PAGE_KEY, { month: m.format("YYYY-MM") });
  };

  /* เนื้อหาในช่องปฏิทินแต่ละวัน */
  const cellRender = (value) => {
    const key = value.format("YYYY-MM-DD");
    const s = summaryMap[key];
    if (!s) return null;

    const late = value.isBefore(dayjs().startOf("day"));

    const sups = supplierMap[key] || [];

    return (
      <ul className="events m-0 p-0" style={{ listStyle: "none" }}>
        <li>
          <Badge
            status={late ? "error" : "warning"}
            text={
              <span style={{ fontSize: 12 }}>
                PO {s.po_count} ใบ / {s.item_count} รายการ
              </span>
            }
          />
        </li>
        {/* ผู้ขายของวันนั้น โชว์ 2 รายแรก ที่เหลือบอกเป็นจำนวน */}
        {sups.slice(0, 2).map((n) => (
          <li
            key={n}
            style={{
              fontSize: 11,
              color: "#595959",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={n}
          >
            {n}
          </li>
        ))}
        {sups.length > 2 && (
          <li style={{ fontSize: 11, color: "#8c8c8c" }}>
            +{sups.length - 2} ราย
          </li>
        )}
      </ul>
    );
  };

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>

      <Card loading={loading}>
        <Flex
          className="width-100 mb-2"
          align="center"
          justify="space-between"
          wrap
          gap={8}
        >
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            รายงาน PO ค้างรับ
          </Typography.Title>
          <Space wrap>
            <Tag color="orange">
              เดือนนี้ค้าง {monthTotal.po} ใบ / {monthTotal.item} รายการ
            </Tag>
          </Space>
        </Flex>

        <Typography.Text type="secondary">
          ช่องในปฏิทินคือวันนัดส่งของที่ยังรับของไม่ครบ — กดที่วันเพื่อดูรายละเอียด
        </Typography.Text>

        <div className="po-pending-cal">
        <Calendar
          value={month}
          mode="month"
          /* หัวปฏิทินของตัวเอง — มีแค่ปุ่มเดือนก่อนหน้า/ถัดไป ไม่มีมุมมองรายปี */
          headerRender={() => {
            /* หัวปฏิทินของตัวเอง : ปุ่มเลื่อนเดือน + เลือกปี/เดือนเอง
               (ไม่มีมุมมองรายปีแบบของ AntD) */
            const years = [];
            for (let y = dayjs().year() - 5; y <= dayjs().year() + 5; y++) {
              years.push({ value: y, label: String(y) });
            }
            const months = Array.from({ length: 12 }, (_, i) => ({
              value: i,
              label: dayjs().month(i).format("MMMM"),
            }));

            return (
              <Flex align="center" justify="center" className="mb-2" wrap gap={8}>
                <Button icon={<LeftOutlined />} onClick={() => changeMonth(-1)}>
                  เดือนก่อนหน้า
                </Button>

                <Select
                  style={{ width: 110 }}
                  value={month.year()}
                  options={years}
                  onChange={(y) => changeMonth(0, month.year(y))}
                />
                <Select
                  style={{ width: 130 }}
                  value={month.month()}
                  options={months}
                  onChange={(m) => changeMonth(0, month.month(m))}
                />

                <Button
                  onClick={() => changeMonth(1)}
                  iconPosition="end"
                  icon={<RightOutlined />}
                >
                  เดือนถัดไป
                </Button>
              </Flex>
            );
          }}
          onPanelChange={(v) => changeMonth(0, v)}
          onSelect={(v) => {
            const key = v.format("YYYY-MM-DD");
            if (summaryMap[key]) setPickedDate(key);
          }}
          cellRender={(value, info) =>
            info.type === "date" ? cellRender(value) : info.originNode
          }
        />
        </div>
      </Card>

      <Modal
        title={
          pickedDate
            ? `PO ค้างรับ วันที่ ${dayjs(pickedDate).format("DD/MM/YYYY")}`
            : ""
        }
        open={!!pickedDate}
        onCancel={() => setPickedDate(null)}
        footer={null}
        width={1100}
      >
        <Space direction="vertical" style={{ display: "flex" }} size="middle">
          <Space wrap>
            <Tag color="orange">{dayItems.length} รายการ</Tag>
          </Space>
          <Table
            size="small"
            bordered
            rowKey={(r, i) => `${r.pocode}-${r.stcode}-${i}`}
            columns={poPendingColumns({ openPo })}
            dataSource={dayItems}
            pagination={false}
            scroll={{ x: "max-content", y: 420 }}
            locale={{ emptyText: <span>ไม่มีรายการค้างรับ</span> }}
          />
        </Space>
      </Modal>
    </Space>
  );
}
